import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  procedureExecutionService,
  type EvidenceType,
  type EvidenceItem,
} from '../services/procedure-execution.service';
import { procedureExecutionKeys } from './useProcedureExecution';

export function useStepEvidenceList(inspectionId: string, stepExecId: string, enabled: boolean) {
  return useQuery({
    queryKey: procedureExecutionKeys.stepEvidence(inspectionId, stepExecId),
    queryFn: () => procedureExecutionService.listEvidence(inspectionId, stepExecId),
    staleTime: 30_000,
    enabled: enabled && !!inspectionId && !!stepExecId,
  });
}

export function useUploadEvidence(inspectionId: string, stepExecId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ evidenceType, file }: { evidenceType: EvidenceType; file: File }) =>
      procedureExecutionService.uploadEvidence(inspectionId, stepExecId, evidenceType, file),

    onSuccess: (newItem) => {
      qc.setQueryData<EvidenceItem[]>(
        procedureExecutionKeys.stepEvidence(inspectionId, stepExecId),
        (prev) => [...(prev ?? []), newItem],
      );
      void qc.invalidateQueries({ queryKey: procedureExecutionKeys.execution(inspectionId) });
    },
  });
}

export function useDeleteEvidence(inspectionId: string, stepExecId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (evidenceId: string) =>
      procedureExecutionService.deleteEvidence(inspectionId, stepExecId, evidenceId),

    onSuccess: (_result, evidenceId) => {
      qc.setQueryData<EvidenceItem[]>(
        procedureExecutionKeys.stepEvidence(inspectionId, stepExecId),
        (prev) => (prev ?? []).filter((e) => e.id !== evidenceId),
      );
      void qc.invalidateQueries({ queryKey: procedureExecutionKeys.execution(inspectionId) });
    },
  });
}

export function useReplaceEvidence(inspectionId: string, stepExecId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ evidenceId, file }: { evidenceId: string; file: File }) =>
      procedureExecutionService.replaceEvidence(inspectionId, stepExecId, evidenceId, file),

    onSuccess: (updated) => {
      qc.setQueryData<EvidenceItem[]>(
        procedureExecutionKeys.stepEvidence(inspectionId, stepExecId),
        (prev) => (prev ?? []).map((e) => (e.id === updated.id ? updated : e)),
      );
    },
  });
}
